import type { Sort } from '@/components/table/sort';
import type { PageRequest } from '@/models/listPage';
import { TIME_SHEET_STATUS } from '@/models/references/references';

/** Filtres communs aux deux listes de la Validation */
export interface SheetFilters {
  /** Chaque mot cherché dans le prénom, le nom ou l'email (côté API) */
  search: string;
  /** Ids des équipes (actuelle pour « À soumettre », figée à la soumission pour « Soumises ») ; vide : toutes */
  teams: string[];
}

/** À l'arrivée : aucun filtre (filtre par défaut selon le rôle à décider plus tard, chef de projet 2026-10-23) */
export const DEFAULT_SHEET_FILTERS: SheetFilters = { search: '', teams: [] };

/**
 * Colonnes triables des « Soumises » : employé, équipe, date de soumission (TimeSheet::submittedAt), total
 * (TimeSheetSummary côté API). « À soumettre » : employé, équipe
 */
export type SheetSortKey = 'name' | 'team' | 'submittedAt' | 'total';

export const DEFAULT_SHEET_SORT: Sort<SheetSortKey> = { key: 'name', direction: 'asc' };

export type ToSubmitSortKey = 'name' | 'team';

export const DEFAULT_TO_SUBMIT_SORT: Sort<ToSubmitSortKey> = { key: 'name', direction: 'asc' };

/** Liste « À soumettre » : courte, 5 par page par défaut */
export const TO_SUBMIT_PAGE_SIZES = [5, 10, 25] as const;

/**
 * Statuts proposés au filtre des soumises : ceux où une feuille reste. Soumise et Refusée sont transitoires (aussitôt
 * en attente, ou de retour en brouillon : API, SubmittedState et RejectedState), visibles dans l'historique seulement
 */
export const SUBMITTED_FILTER_STATUSES: readonly string[] = [
  TIME_SHEET_STATUS.PENDING_MANAGER,
  TIME_SHEET_STATUS.PENDING_DIRECTOR,
  TIME_SHEET_STATUS.VALIDATED,
];

/** Paramètre d'URL des statuts de départ des « Soumises » (/validation?status=PENDING_MANAGER&status=…) */
export const STATUS_PARAM = 'status';

/** Lien vers la Validation, « Soumises » filtrées sur ces statuts (ex. tuiles du Tableau de bord) */
export function validationLink(statuses: string[]): string {
  const params = new URLSearchParams(statuses.map((status) => [STATUS_PARAM, status]));
  return `/validation?${params}`;
}

/** Statuts de départ des « Soumises », lus dans l'URL : ceux du filtre seulement, sans doublon */
export function statusesFromSearch(search: URLSearchParams): string[] {
  return [...new Set(search.getAll(STATUS_PARAM))].filter((status) => SUBMITTED_FILTER_STATUSES.includes(status));
}

/**
 * Requête GET /team_members d'une page de « À soumettre » : l'appartenance active (une seule par employé) des employés
 * attendus sur la période ouverte qui n'ont pas encore soumis, aucune feuille ou un brouillon (toSubmit, côté API : les
 * feuilles ne sont pas créées d'avance), par équipe, par NOM puis prénom ou par équipe (puis par NOM et prénom). null
 * sans période ouverte.
 */
export function buildToSubmitRequest(
  periodId: number | null,
  { search, teams }: SheetFilters,
  { key, direction }: Sort<ToSubmitSortKey>,
  page: number,
  itemsPerPage: number,
): PageRequest | null {
  if (!periodId) return null;
  const byName = { 'member.user.lastName': direction, 'member.user.firstName': direction };

  return {
    filters: { toSubmit: periodId, search: search.trim() || null, team: teams.length ? teams : null },
    order:
      key === 'team'
        ? { 'team.name': direction, 'member.user.lastName': 'asc', 'member.user.firstName': 'asc' }
        : byName,
    page,
    itemsPerPage,
  };
}

/**
 * Requête GET /time_sheets d'une page des « Soumises », sur la période ouverte : tout sauf les brouillons
 * (not[timeSheetStatus], NotInFilter d'api-extension), par NOM puis prénom, par équipe, par date de soumission ou par
 * total (puis par NOM et prénom). null sans période ouverte, ou tant que les statuts ne sont pas chargés.
 */
export function buildSheetRequest(
  periodId: number | null,
  { search, teams }: SheetFilters,
  /** Références des statuts ; vide : tous sauf le brouillon */
  statuses: string[],
  sort: Sort<SheetSortKey>,
  page: number,
  itemsPerPage: number,
  /** Id API d'un statut (liste chargée depuis l'API) */
  statusId: (reference: string) => number | null,
  /** Les feuilles qui attendent l'utilisateur (?awaitingMe, règle côté API) ; les statuts sont alors ignorés */
  awaitingMe = false,
): PageRequest | null {
  if (!periodId) return null;
  const draftId = statusId(TIME_SHEET_STATUS.DRAFT);
  const statusIds = statuses.map(statusId);
  if (draftId === null || statusIds.includes(null)) return null;
  const { direction } = sort;
  const byName = { 'member.user.lastName': direction, 'member.user.firstName': direction };
  const thenByName = { 'member.user.lastName': 'asc', 'member.user.firstName': 'asc' } as const;

  return {
    filters: {
      period: periodId,
      search: search.trim() || null,
      'teamMember.team': teams.length ? teams : null,
      'not[timeSheetStatus]': [draftId],
      timeSheetStatus: !awaitingMe && statusIds.length ? (statusIds as number[]) : null,
      awaitingMe: awaitingMe ? 'true' : null,
    },
    // Égalités (même équipe, même total) départagées par employé, comme le récapitulatif
    order:
      sort.key === 'team'
        ? { 'teamMember.team.name': direction, ...byName }
        : sort.key === 'submittedAt'
          ? { submittedAt: direction, ...thenByName }
          : sort.key === 'total'
            ? { 'timeSheetSummaries.totalMinutes': direction, ...thenByName }
            : byName,
    page,
    itemsPerPage,
  };
}
